import { useState, useEffect, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import {
  Phone, Mail, Building, ChevronDown, ChevronUp, MessageCircle,
  PhoneIncoming, PhoneOutgoing, MailOpen, Send, Loader2,
  Clock, User, ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  call: { icon: Phone, label: 'Appel', color: 'text-cyan-500 bg-cyan-500/10 border-cyan-500/20' },
  email: { icon: Mail, label: 'Email', color: 'text-rose-500 bg-rose-500/10 border-rose-500/20' },
  sms: { icon: MessageCircle, label: 'SMS', color: 'text-green-500 bg-green-500/10 border-green-500/20' },
  whatsapp: { icon: MessageCircle, label: 'WhatsApp', color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20' },
};

function DirectionIcon({ type, direction }) {
  if (type === 'call') {
    return direction === 'inbound'
      ? <PhoneIncoming className="w-3.5 h-3.5 text-blue-400" />
      : <PhoneOutgoing className="w-3.5 h-3.5 text-orange-400" />;
  }
  if (type === 'email') {
    return direction === 'inbound'
      ? <MailOpen className="w-3.5 h-3.5 text-blue-400" />
      : <Send className="w-3.5 h-3.5 text-orange-400" />;
  }
  return null;
}

function formatDate(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));

  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  if (diffDays === 0) return `Aujourd'hui ${time}`;
  if (diffDays === 1) return `Hier ${time}`;
  if (diffDays < 7) return `${d.toLocaleDateString('fr-FR', { weekday: 'long' })} ${time}`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined }) + ` ${time}`;
}

function groupByDate(items) {
  const groups = {};
  for (const item of items) {
    const d = new Date(item.timestamp);
    const key = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    if (!groups[key]) groups[key] = [];
    groups[key].push(item);
  }
  return groups;
}

function TimelineItem({ item }) {
  const [expanded, setExpanded] = useState(false);
  const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.call;
  const Icon = config.icon;
  const hasFull = item.memory_text && item.memory_text.length > 0;
  const summaryText = item.summary || (item.memory_text ? item.memory_text.substring(0, 150) + '...' : 'Pas de contenu');

  return (
    <div className="relative pl-8 pb-5 group" data-testid={`timeline-item-${item.id}`}>
      {/* Timeline line */}
      <div className="absolute left-[13px] top-8 bottom-0 w-px bg-border group-last:hidden" />

      {/* Icon dot */}
      <div className={`absolute left-0 top-1 flex items-center justify-center w-7 h-7 rounded-full border ${config.color}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>

      <div className="space-y-1">
        {/* Header */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium">{config.label}</span>
          <DirectionIcon type={item.type} direction={item.direction} />
          {item.person_name && (
            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
              <User className="w-2.5 h-2.5" /> {item.person_name}
            </span>
          )}
          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5 ml-auto">
            <Clock className="w-2.5 h-2.5" /> {formatDate(item.timestamp)}
          </span>
        </div>

        {/* Summary */}
        <p className="text-xs text-muted-foreground leading-relaxed">
          {summaryText}
        </p>

        {/* Expand button */}
        {hasFull && (
          <>
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-[10px] text-primary/70 hover:text-primary transition-colors pt-0.5"
              data-testid={`expand-item-${item.id}`}
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? 'Reduire' : 'Voir le contenu complet'}
            </button>
            {expanded && (
              <div className="mt-2 p-3 rounded-lg bg-muted/50 border text-xs text-foreground/80 leading-relaxed whitespace-pre-wrap max-h-96 overflow-y-auto" data-testid={`expanded-content-${item.id}`}>
                {item.memory_text}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function ContactDetailPanel({ contact, open, onOpenChange, token }) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchTimeline = useCallback(async () => {
    if (!contact?.id || !token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/contacts/${contact.id}/timeline`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, per_page: 30 }
      });
      setTimeline(data.items || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch {
      setTimeline([]);
    } finally {
      setLoading(false);
    }
  }, [contact?.id, token, page]);

  useEffect(() => {
    if (open && contact?.id) {
      setPage(1);
    }
  }, [open, contact?.id]);

  useEffect(() => {
    if (open) fetchTimeline();
  }, [open, fetchTimeline]);

  if (!contact) return null;

  const initials = `${(contact.first_name || '?')[0]}${(contact.last_name || '?')[0]}`.toUpperCase();
  const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
  const grouped = groupByDate(timeline);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col" data-testid="contact-detail-panel">
        {/* Header */}
        <div className="p-5 border-b space-y-3 shrink-0">
          <SheetHeader>
            <div className="flex items-center gap-3">
              <Avatar className="h-11 w-11">
                <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <SheetTitle className="text-base truncate" data-testid="contact-detail-name">{fullName}</SheetTitle>
                <SheetDescription className="truncate">
                  {contact.job_title && <span>{contact.job_title}</span>}
                  {contact.job_title && (contact.company || contact.organization_name) && <span> · </span>}
                  {(contact.company || contact.organization_name) && (
                    <span className="inline-flex items-center gap-0.5">
                      <Building className="w-3 h-3 inline" /> {contact.organization_name || contact.company}
                    </span>
                  )}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          {/* Contact info pills */}
          <div className="flex flex-wrap gap-1.5">
            {(contact.phones?.length > 0 ? contact.phones : contact.phone ? [{ number: contact.phone, label: 'principal' }] : []).map((p, i) => (
              <Badge key={i} variant="outline" className="text-[10px] px-2 py-0.5 gap-1 font-normal">
                <Phone className="w-3 h-3" /> {p.number}
                {p.label && p.label !== 'principal' && <span className="opacity-50">({p.label})</span>}
              </Badge>
            ))}
            {(contact.emails?.length > 0 ? contact.emails : contact.email ? [{ address: contact.email }] : []).map((e, i) => (
              <Badge key={i} variant="outline" className="text-[10px] px-2 py-0.5 gap-1 font-normal">
                <Mail className="w-3 h-3" /> {e.address}
              </Badge>
            ))}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span data-testid="timeline-total">{total} interaction{total !== 1 ? 's' : ''}</span>
            {contact.source_badges?.length > 0 && (
              <div className="flex gap-1">
                {contact.source_badges.map(s => (
                  <Badge key={s} variant="secondary" className="text-[9px] px-1.5 py-0">{s}</Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Timeline */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="p-5">
            {loading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground" data-testid="timeline-loading">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
              </div>
            ) : timeline.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground text-sm" data-testid="timeline-empty">
                Aucune interaction enregistree
              </div>
            ) : (
              Object.entries(grouped).map(([date, items]) => (
                <div key={date} className="mb-4">
                  <div className="sticky top-0 bg-background/95 backdrop-blur-sm z-10 pb-2 mb-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                      {date}
                    </span>
                  </div>
                  {items.map(item => (
                    <TimelineItem key={item.id} item={item} />
                  ))}
                </div>
              ))
            )}

            {/* Pagination */}
            {totalPages > 1 && !loading && (
              <div className="flex items-center justify-between pt-3 border-t" data-testid="timeline-pagination">
                <span className="text-[10px] text-muted-foreground">
                  Page {page}/{totalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="h-7 text-xs gap-0.5 px-2"
                    data-testid="timeline-prev"
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="h-7 text-xs gap-0.5 px-2"
                    data-testid="timeline-next"
                  >
                    <ChevronRight className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
